import AnimationFadeIn from "@components/AnimationFadeIn";
import BackgroundImageCst from "@components/BackgroundImageCst";
import Breadcrumb from "@components/Breadcrumb";
import ButtonBackPage from "@components/ButtonBackPage";
import HeroCst from "@components/HeroCst";
import Table from "@components/Table";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";
import { useFetchTable } from "@hooks/useFetchDataTable";
import { useFetchDataDetail, useFetchDetail } from "@hooks/useFetchDetail";
import DownloadIcon from "@mui/icons-material/Download";
import { Box, Container, useTheme } from "@mui/material";
import { downloadBlobFile, onErrorMessage, ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { CollectCampaignDto } from "../../types";
import { ActionsType } from "src/interfaces/Common";
import CustomRowAction from "@components/Table/components/CustomRowAction";
import { MRT_RowSelectionState } from "material-react-table";
import { MethodType } from "src/services/type";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import CustomTopToolbar from "@components/Table/components/CustomTopToolbar";
import { buildDateStr } from "@utils/utilsDate";
import FormLoadingStateCst from "@components/FormLoadingStateCst";

const MonitoringUser: React.FC<{ page: PagesID }> = ({ page }) => {
  const { t, i18n } = useTranslation(["collect-campaign-page"]);
  const { t: inputTranslations } = useTranslation(["input"]);
  const theme = useTheme();
  const { id, idCampaign, idUser } = useParams();
  const { filters } = useFilters();
  const { user } = useAuth();
  const navigate = useNavigate();
  const filterId = `${user?.id_user}_${page}`;
  const [checkBoxData, setCheckBoxData] = useState<any>([]);
  const handleCheckBox = (row: any) => {
    let dataBox = checkBoxData;
    let setData = setCheckBoxData;
    if (dataBox && dataBox.some((el: any) => el.id === row.id)) {
      const removeData = dataBox.filter((el: any) => el.id !== row.id);
      setData(removeData);
    } else {
      setData([...dataBox, row]);
    }
  };
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
  }>();
  const { dataDetail: userDetail } = useFetchDetail(
    ServicesURL.customer,
    (idUser || "") + `?corporateId=${id}`,
    PagesID["corporate.collect-campaign.monitor.user"],
  );

  const { dataDetail: campaign } = useFetchDataDetail(
    //GET NOTIFICATION DETAILS
    ServicesURL.getCampaign
      .replace(":corporateId", id ? id : "1")
      .replace(":campaignId", idCampaign ?? ""),
    PagesID["corporate.collect-campaign.monitor.user"],
  );

  const { columns, dataTable, actionForRow, actionsTopToolbar, isLoading } =
    useFetchTable(
      ServicesURL.getCampaignUserLos
        .replace(":corporateId", id ? id : "1")
        .replace(":campaignId", idCampaign ?? "")
        .replace(":userId", idUser ?? ""),
      PagesID["corporate.collect-campaign.monitor.user"],
      filters[filterId],
    );

  const filteredColumns = columns?.filter((col) => {
    return (
      !["points_formatted", "credits_formatted", "hours_formatted"].includes(
        col.accessorKey,
      ) ||
      ((campaign as CollectCampaignDto).unit === "HOURS" &&
        col.accessorKey === "hours_formatted") ||
      ((campaign as CollectCampaignDto).unit === "POINTS" &&
        col.accessorKey === "points_formatted") ||
      ((campaign as CollectCampaignDto).unit === "CREDITS" &&
        col.accessorKey === "credits_formatted")
    );
  });

  const formatNumber = (value?: number): string =>
    Number.isInteger(value) ? (value ?? 0).toString() : (value ?? 0).toFixed(2);

  const tableData = useMemo(() => {
    const target = formatNumber((campaign as CollectCampaignDto).target);
    return dataTable?.output.map((row: any) => {
      const newRow = { ...row };
      if ((campaign as CollectCampaignDto).unit === "HOURS") {
        newRow.hours_formatted = `${formatNumber(row.hours)}/${target}`;
      }
      if ((campaign as CollectCampaignDto).unit === "POINTS") {
        newRow.points_formatted = `${formatNumber(row.points)}/${target}`;
      }
      if ((campaign as CollectCampaignDto).unit === "CREDITS") {
        newRow.credits_formatted = `${formatNumber(row.credits)}/${target}`;
      }
      if (
        row.type === "COURSE" ||
        row.type === "PATH" ||
        row.type === "ACTIVITY"
      ) {
        newRow.type = inputTranslations(
          `options.lo_types.${row.type.toLowerCase()}`,
        );
      } else {
        newRow.type = inputTranslations(
          `options.activity_type.${row.type.toLowerCase()}`,
        );
      }
      return newRow;
    });
  }, [dataTable, campaign, inputTranslations]);

  const actionsRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      switch (action.key) {
        case "download_certificate": {
          return {
            ...action,
            callBack: async (row: any) => {
              const payload = {
                learnerId: idUser,
                objectId: row.id,
                certificateId: row.certificate_id,
              };

              try {
                setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
                const url = `${ServicesURL.getIssuedCertificate.replace(":certificateId", payload.certificateId)}?objectId=${payload.objectId}&learnerId=${payload.learnerId}`;
                await downloadBlobFile(
                  url,
                  null,
                  `issued_certificate_lo_${payload.objectId}_learner_${payload.learnerId}.pdf`,
                  MethodType.GET,
                );
                //await triggerDownloadCertificate(payload);
                setOpenToast({ s: StatusEnum.OK, message: "" });
              } catch (error) {
                const message = onErrorMessage(error);
                setOpenToast({ s: StatusEnum.KO, message: message });
                console.error(error);
              }
            },
            getIcon: (row: any) => {
              console.info(
                !!row.certificate_id,
                row.certificate_id,
                !!row.certificate_id ? "primary" : undefined,
              );
              return (
                <DownloadIcon
                  color={!!row.certificate_id ? "primary" : undefined}
                  sx={
                    !!row.certificate_id
                      ? {
                          "&.MuiSvgIcon-root": {
                            fill: `${theme.palette.primary.main}!important`,
                          },
                        }
                      : undefined
                  }
                />
              );
            },
          };
        }
        default:
          return action;
      }
    });
  }, [actionForRow]);

  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = actionsRow && [...actionsRow];

    configRowNew = configRowNew.map((el: ActionsType) => {
      if (el.type === "delete") {
        // return { ...el, disabled: !row.original.deletable };
        return { ...el };
      }

      if (el.type === "edit") {
        // return { ...el, disabled: !row.original.editable };
        return { ...el };
      }
      if (el.key === "download_certificate") {
        return { ...el, disabled: !row.original.certificate_id };
      }
      return { ...el };
    });
    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
        checkBoxData={[checkBoxData]}
        setCheckBoxData={handleCheckBox}
      />,
    ];
  };

  const customRenderTopToolbar = ({
    table,
  }: {
    table: any;
  }): React.ReactNode => {
    return columns ? (
      <CustomTopToolbar
        id={page}
        key={`custom_top_toolbar_${page}`}
        columns={columns
          .filter((column) => (column as any)["hasFilter"])
          .map((column) => ({
            ...column,
            visibility: true,
            visibilityInTable: true,
          }))}
        configurationTopToolbar={actionsToolbar}
        table={table}
        hiddenFilters={false}
        resetFormFilters={undefined}
        setHideHeader={undefined}
        counterElements={false}
        onToolbarChange={() => {}}
        modalTitle={undefined}
        noSummarySelected={undefined}
        filtersException={undefined}
        enableFullScreenToggle={false}
      />
    ) : (
      <></>
    );
  };

  const actionsToolbar: ActionsType[] | undefined = React.useMemo(() => {
    return actionsTopToolbar?.map((action: ActionsType) => {
      switch (action.type) {
        case "button": {
          if (action.type === "button" && action.icon === "download") {
            const hasDownloadCertificatePermission = user?.permissions.find(
              (userPermission) => userPermission === action.permission,
            );
            if (!hasDownloadCertificatePermission) {
              return {} as ActionsType;
            }
            return {
              ...action,
              labelTranslate: (action.label as any)[i18n.language],
              callBack: checkBoxData.length
                ? async (el: any) => {
                    try {
                      setOpenToast({
                        s: StatusEnum.LOADING,
                        message: "loading",
                      });

                      const fileName = `certificates_${buildDateStr(new Date())}.zip`;

                      const issuedCertificates = checkBoxData
                        .filter((row: any) => !!row.certificate_id)
                        .map((row: any) => {
                          return {
                            learnerId: idUser,
                            objectId: row.id,
                            issuedCertificateId: row.certificate_id,
                          };
                        });

                      await downloadBlobFile(
                        `${ServicesURL.getIssuedCertificatesZip}`,
                        {
                          issuedCertificates: issuedCertificates,
                        },
                        fileName,
                      );
                      setOpenToast({ s: StatusEnum.OK, message: "" });
                    } catch (error) {
                      const message = onErrorMessage(error);
                      setOpenToast({ s: StatusEnum.KO, message: message });
                      console.error(error);
                    }
                  }
                : null,
              getIcon: () => <DownloadIcon />,
            } as ActionsType;
          }
          return {
            ...action,
            labelTranslate: (action.label as any)[i18n.language],
          } as ActionsType;
        }
        default:
          return action;
      }
    });
  }, [actionsTopToolbar, i18n, user?.permissions, checkBoxData, idUser]);

  const renderCell = (cel: any, value: any, row: any) => {
    return value !== undefined && value !== null ? value.toString() : "";
  };

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-survey.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={t("campaign-user.hero-title", {
            name: userDetail.name,
            surname: userDetail.surname,
          })}
          description={""}
        />
        <ButtonBackPage
          customRedirect={`/corporate/${id}/detail/collect-campaigns/monitor-campaign/${idCampaign}`}
        />
        <Box
          sx={{
            mt: "25px",
          }}
        >
          {isLoading ? (
            <FormLoadingStateCst />
          ) : (
            <Table
              id={page}
              data={tableData || []}
              columns={filteredColumns || []}
              // configurationTopToolbar={actionsToolbar}
              configurationActionForRow={actionsRow}
              renderRowActions={customRenderRowActions}
              renderTopToolbar={customRenderTopToolbar}
              totalPages={dataTable?.metadata?.total_elements}
              renderCell={renderCell}
            />
          )}
        </Box>
      </Container>
      <ModalConfirmsCst
        open={!!openToast}
        title={t("download-certificates.title", { ns: "modal" })}
        description={""}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        removeButtons={true}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};
export default MonitoringUser;
